import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
} from 'axios'
import { silentRenew } from '@/features/auth/oidcClient'
import { TOKEN_KEY } from '@/constant/common'
import { normalizeApiError } from '@/helpers/apiError'

const axiosClient = axios.create({
  baseURL: import.meta.env.VITE_URL_API,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Identity Server client: auth/user/org/role/permissions endpoints
export const identityClient = axios.create({
  baseURL: import.meta.env.VITE_URL_IDENTITY_API,
  headers: {
    'Content-Type': 'application/json',
  },
})

export const telemetryClient = axios.create({
  baseURL: import.meta.env.VITE_TELEMETRY_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

export const setToken = (token: string) => {
  const authHeader = `Bearer ${token}`
  axiosClient.defaults.headers.common['Authorization'] = authHeader
  identityClient.defaults.headers.common['Authorization'] = authHeader
  telemetryClient.defaults.headers.common['Authorization'] = authHeader
}
export const removeToken = () => {
  delete axiosClient.defaults.headers.common['Authorization']
  delete identityClient.defaults.headers.common['Authorization']
  delete telemetryClient.defaults.headers.common['Authorization']
}

export const createAxiosInstance = ({
  baseURL,
  apiKey,
}: {
  baseURL?: string
  apiKey?: string
} = {}) => {
  const instance = axios.create({
    baseURL: baseURL ?? axiosClient.defaults.baseURL,
    headers: {
      ...axiosClient.defaults.headers.common,
      ...(apiKey ? { 'x-api-key': apiKey } : {}),
    },
  })

  instance.interceptors.response.use(
    (response) => response.data,
    (error: unknown) => Promise.reject(normalizeApiError(error)),
  )

  return instance
}

// ── Shared refresh-token state ──────────────────────────────────────────
// Prevents multiple concurrent refresh attempts when several requests
// fail with 401 at the same time.
let isRefreshing = false
let failedQueue: {
  resolve: (token: string) => void
  reject: (error: unknown) => void
}[] = []

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (token) {
      prom.resolve(token)
    } else {
      prom.reject(error)
    }
  })
  failedQueue = []
}

const forceLogout = () => {
  removeToken()
  localStorage.clear()
  if (window.location.pathname !== '/') {
    window.location.href = '/'
  }
}

/**
 * Shared 401 response interceptor for both axiosClient & identityClient.
 * On 401 → attempt silent token renewal via OIDC refresh_token.
 * If renewal succeeds → retry the original request with the new token.
 * If renewal fails → clear session and redirect to login.
 */
const handle401Error = async (
  error: AxiosError,
  client: AxiosInstance,
): Promise<AxiosResponse> => {
  const originalRequest = error.config as AxiosRequestConfig & {
    _retry?: boolean
  }

  // Skip refresh logic on /callback page or if request was already retried
  if (
    !originalRequest ||
    originalRequest._retry ||
    window.location.pathname === '/callback'
  ) {
    throw error
  }

  if (error.response?.status !== 401) {
    throw error
  }

  // If already refreshing, queue this request until refresh completes
  if (isRefreshing) {
    return new Promise<string>((resolve, reject) => {
      failedQueue.push({ resolve, reject })
    }).then((newToken) => {
      originalRequest.headers!['Authorization'] = `Bearer ${newToken}`
      return client(originalRequest)
    })
  }

  originalRequest._retry = true
  isRefreshing = true

  try {
    const user = await silentRenew()

    if (!user?.access_token) {
      throw new Error('Silent renew returned no access token')
    }

    const newToken = user.access_token
    setToken(newToken)
    localStorage.setItem(TOKEN_KEY, newToken)

    processQueue(null, newToken)

    // Retry the original failed request with the new token
    originalRequest.headers![`Authorization`] = `Bearer ${newToken}`
    return client(originalRequest)
  } catch (refreshError) {
    processQueue(refreshError, null)
    forceLogout()
    throw refreshError
  } finally {
    isRefreshing = false
  }
}

const installResponseInterceptors = (client: AxiosInstance) => {
  client.interceptors.response.use(
    (response) => response,
    async (error: unknown) => {
      try {
        if (axios.isAxiosError(error) && error.response?.status === 401) {
          return await handle401Error(error, client)
        }
        throw error
      } catch (handledError) {
        throw normalizeApiError(handledError)
      }
    },
  )
}

installResponseInterceptors(axiosClient)
installResponseInterceptors(identityClient)
installResponseInterceptors(telemetryClient)

export default axiosClient
